import {
  createBrowserRouter,
  isRouteErrorResponse,
  matchRoutes,
  RouterProvider,
  useLocation,
  useRouteError,
} from 'react-router-dom';
import type {ReactNode} from 'react';
import {AppDraftGuardProvider} from './AppDraftGuard.js';

const APP_ROOT_PATH = '*';

function AppRouteError({application}: {application: ReactNode}) {
  const error = useRouteError();
  const location = useLocation();
  // React Router decodes before matching; its wildcard excludes line terminators.
  // Preserve those raw URLs for App's existing terminal-route owner. Only a
  // router-unmatched 404 takes this fallback, never a matched route's error.
  if (isRouteErrorResponse(error) && error.status === 404 && matchRoutes([{path: APP_ROOT_PATH}], location) === null) {
    return application;
  }
  // Keep the existing outer ErrorBoundary's reporting and recovery UI. A data
  // router's default error page must not replace that application boundary.
  throw error;
}

export function appRootRoutes(children: ReactNode) {
  const application = <AppDraftGuardProvider>{children}</AppDraftGuardProvider>;
  return [
    {
      path: APP_ROOT_PATH,
      element: application,
      errorElement: <AppRouteError application={application} />,
    },
  ];
}

/** Construct once, after initRuntime has normalized legacy bookmarks. */
export function createAppBrowserRouter(children: ReactNode) {
  return createBrowserRouter(appRootRoutes(children));
}

export function AppNavigationRoot({router}: {router: ReturnType<typeof createAppBrowserRouter>}) {
  return <RouterProvider router={router} />;
}
